import React, { PropsWithChildren, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import { useHover } from '@use-gesture/react';
import { theme } from '../../theme';
import { CrossIcon, SvgComponent } from '../../assets';
import { UIText } from '../typography';

const Button = styled(animated.button)`
  border: none;
  background-color: ${theme.colors.primary.graysBlue};
  padding: 0;
  min-width: 40px;
  height: 40px;
  border-radius: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  * > {
    margin-left: 8px;
    margin-right: 8px;
  }
`;

const TextContainer = styled(animated.div)`
  overflow: hidden;
  white-space: nowrap;
`;

const Text = styled(UIText)`
  font-weight: bold;
  color: ${theme.colors.primary.white};
`;

const Close = styled(CrossIcon).attrs({ width: 16 })`
  fill: ${theme.colors.primary.white};
`;

export type CircleButtonProps = {
  icon: SvgComponent;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isExpanded: boolean;
  isHidden?: boolean;
  doesExpand?: boolean;
  maxWidth?: string;
  iconSize?: number;
  className?: string;
};

const getIconStyles = (isExpanded: boolean) => ({
  opacity: isExpanded ? 0 : 1,
  maxWidth: isExpanded ? '0px' : '20px',
  marginLeft: '0px',
  display: 'flex',
});

const getTextStyles = (isExpanded: boolean, maxWidth: string) => ({
  opacity: isExpanded ? 1 : 0,
  maxWidth: isExpanded ? maxWidth : '0px',
  marginLeft: isExpanded ? '12px' : '0px',
  marginRight: isExpanded ? '12px' : '0px',
});

const getCloseStyles = (isExpanded: boolean) => ({
  opacity: isExpanded ? 1 : 0,
  maxWidth: isExpanded ? '20px' : '0px',
  marginRight: isExpanded ? '12px' : '0px',
  display: 'flex',
});

export const CircleButton: React.FC<PropsWithChildren<CircleButtonProps>> = ({
  icon: Icon,
  onClick,
  isExpanded,
  isHidden = false,
  doesExpand = true,
  maxWidth = '200px',
  iconSize = 20,
  className,
  children,
}) => {
  const domTarget = useRef(null);

  const buttonStyles = useSpring({
    minWidth: isHidden ? '0px' : '40px',
    maxWidth: isHidden ? '0px' : maxWidth,
    maxHeight: isHidden ? '0px' : '40px',
    overflow: 'hidden',
  });

  const [iconStyles, iconStylesApi] = useSpring(() =>
    getIconStyles(isExpanded)
  );

  const [textStyles, textStylesApi] = useSpring(() =>
    getTextStyles(isExpanded, maxWidth)
  );

  const [closeStyles, closeStylesApi] = useSpring(() =>
    getCloseStyles(isExpanded)
  );

  useEffect(() => {
    iconStylesApi.start(getIconStyles(isExpanded));
    textStylesApi.start(getTextStyles(isExpanded, maxWidth));
    closeStylesApi.start(getCloseStyles(isExpanded));
  }, [isExpanded, iconStylesApi, textStylesApi, closeStylesApi, maxWidth]);

  useHover(
    ({ hovering }) => {
      if (isExpanded || !doesExpand) return;
      iconStylesApi.start({
        marginLeft: hovering ? '7px' : '0px',
      });
      textStylesApi.start({
        opacity: hovering ? 1 : 0,
        maxWidth: hovering ? maxWidth : '0px',
        marginLeft: hovering ? '12px' : '0px',
        marginRight: hovering ? '12px' : '0px',
      });
    },
    { target: domTarget }
  );

  return (
    <Button
      ref={domTarget}
      className={className}
      onClick={onClick}
      style={buttonStyles}
    >
      <animated.div style={iconStyles}>
        <Icon fill="white" width={iconSize} />
      </animated.div>
      <TextContainer style={textStyles}>
        <Text>{children}</Text>
      </TextContainer>
      <animated.div style={closeStyles}>
        <Close />
      </animated.div>
    </Button>
  );
};
